import React, { useEffect, useState, useRef, useContext } from "react";
import { makeStyles } from "@mui/styles";
import {
  Box,
  LinearProgress,
  Typography,
  Avatar,
  Badge,
  IconButton,
  Link,
} from "@mui/material";
import { RiChat1Fill } from "react-icons/ri";
import ChatInput from "../ChatInput";
import MessageFeed from "../ChatMessageFeed";
import styles from "../styles";
import "./styles.css";
import { SocketContext } from "../../../contexts/SocketContext";
import { Close } from "@mui/icons-material";
import { useConfig } from "../../../contexts/ConfigContext";
import useGeolocation from "../../../hooks/useGeolocation";
import ChatConfig from "../ChatConfig";
import { useChatWindowSettings } from "../../../contexts/ChatWindowContext";
import {
  DESKTOP_BORDER_RADIUS,
  MOBILE_BORDER_RADIUS,
} from "../../../constants";

const useStyles = makeStyles(styles);

function Chat() {
  const classes = useStyles();
  const { config } = useConfig();
  const { isMobile, setOpen, open } = useChatWindowSettings();
  const {
    getHistorySnapshot,
    emitMessage,
    handleFeedbackSubmit,
    saveChatHistory,
    setBotMessageLoading,
    setHistoryLoading,
    messageHistory,
    isStreaming,
    isConnected,
    isLoading,
    isLoadingHistory,
    resetSession,
  } = useContext(SocketContext);

  useGeolocation(open && !config.geolocationOff);

  const messageHistoryRef = useRef(messageHistory);

  const [message, setMessage] = useState("");
  const [headerImageError, setHeaderImageError] = useState(false);

  useEffect(() => {
    if (!messageHistory) return;
    messageHistoryRef.current = messageHistory;
  }, [messageHistory]);

  const loadHistory = () => {
    setHistoryLoading(true);
    getHistorySnapshot(messageHistoryRef.current?.messages[0].timestamp, 15);
  };

  const scrollCallback = (position, callback) => {
    if (
      position.scrollTop < 10 &&
      messageHistory?.messages_before &&
      !isLoadingHistory
    ) {
      callback(position.scrollHeight);
      loadHistory();
    }
  };

  const onMessageChange = (value) => {
    setMessage(value);
  };

  const onSubmitMessage = () => {
    if (isLoading || isStreaming || !message) return;
    setBotMessageLoading(true);
    emitMessage("send_message", {
      message,
    });
    saveChatHistory([
      ...messageHistory.messages,
      {
        origin: "user",
        message,
      },
      {
        origin: "loader",
      },
    ]);
    setMessage("");
  };

  return (
    <Box
      data-state={isStreaming || isLoading ? "disabled" : "enabled"}
      className={classes.chatHolder}
      sx={{
        borderRadius: isMobile ? MOBILE_BORDER_RADIUS : DESKTOP_BORDER_RADIUS,
      }}
    >
      {isConnected ? (
        <Box position="relative" sx={{ width: "100%", height: "100%" }}>
          {isLoadingHistory && (
            <LinearProgress
              sx={{
                height: 6,
                position: "absolute",
                top: -1,
                left: 0,
                width: "100%",
                zIndex: 1000,
              }}
            />
          )}
          <Box className={classes.mainChatWrapper}>
            <Box
              className={classes.chatHeader}
              sx={{
                backgroundColor: config.color.headerBackground,
                borderRadius: isMobile ? "4px" : "16px 16px 10px 10px",
              }}
            >
              <Badge
                variant="dot"
                color={isConnected ? "success" : "error"}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    mr: 1.5,
                    background: config.color.avatarBackground,
                  }}
                >
                  { config.botAvatarImage && !headerImageError ? <img
                    style={{ maxWidth: "27px", maxHeight: "27px" }}
                    alt="logo"
                    src={config.botAvatarImage}
                    onError={() => setHeaderImageError(true)}
                  ></img> :
                    <RiChat1Fill color={config?.color.chatButtonIconColor} fontSize={22} />
                  }
                </Avatar>
              </Badge>
              <Typography
                variant="subtitle2"
                sx={{ color: config.color.headerText }}
              >
                {config.botName}
              </Typography>
              {isMobile && (
                <IconButton onClick={() => setOpen(false)} sx={{ ml: "auto" }}>
                  <Close />
                </IconButton>
              )}
              {config.metadataOptions && <ChatConfig />}
            </Box>

            <MessageFeed
              messageHistory={messageHistory}
              handleFeedbackSubmit={handleFeedbackSubmit}
              isStreaming={isStreaming}
              scrollCallback={scrollCallback}
            />
            <Box className={classes.inputWrapper}>
              <ChatInput
                message={message}
                onMessageChange={onMessageChange}
                onSubmit={onSubmitMessage}
              />
            </Box>
            <Link
              sx={{
                position: "absolute",
                bottom: 0,
                left: "50%",
                transform: "translateX(-50%)",
                cursor: "pointer",
                opacity: 0.8,
                fontSize: 13,
                "&:hover": { opacity: 1 },
              }}
              color="text.primary"
              onClick={resetSession}
            >
              Reset Conversation
            </Link>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            color: "text.secondary",
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
          }}
        >
          <LinearProgress
            sx={{
              height: 6,
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
            }}
          />
          Waiting for the server...
        </Box>
      )}
    </Box>
  );
}

export default Chat;
